import { add, Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["codeInput"]
  static values = {
    autoSubmit: { type: Boolean, default: true }
  }

  connect() {
    this.createCodeInputs()
    addEventListener("turbo:render", this.createCodeInputs.bind(this))
  }

  disconnect() {
    removeEventListener("turbo:render", this.createCodeInputs.bind(this))
  }

  createCodeInputs() {
    if (this.element.querySelector(".code-input-container")) {
      return // Code inputs already created
    }
    const numberOfInputs = 6
    const container = document.createElement("div")
    container.classList.add("code-input-container", "flex", "gap-2")
    container.dataset.action = "input->code-input#handleInput keydown->code-input#handleKeydown focus->code-input#handleFocus:capture paste->code-input#handlePaste"

    for (let i = 0; i < numberOfInputs; i++) {
      const input = document.createElement("input")
      input.id = `code-input-${i}`
      input.type = "text"
      input.inputMode = "numeric"
      input.pattern = "[0-9]*"
      input.maxLength = 1
      input.classList.add("code-input", "border", "rounded", "w-12", "h-16", "text-center", "text-2xl")
      container.appendChild(input)
    }

    const firstInput = container.querySelector(".code-input")
    if (firstInput) {
      firstInput.autocomplete = "one-time-code"
      requestAnimationFrame(() => {
        firstInput.focus()
      })
    }

    this.codeInputTarget.parentNode.insertBefore(container, this.codeInputTarget)
  }

  handleInput(event) {
    const input = event.target
    const nextInput = input.nextElementSibling

    if (input.value.length === 1 && nextInput && nextInput.classList.contains("code-input")) {
      nextInput.focus()
    }

    this.updateCodeInput()
  }

  handleKeydown(event) {
    const input = event.target

    if (event.key === "Backspace") {
      if (input.value === "") {
        const previousInput = input.previousElementSibling
        if (previousInput && previousInput.classList.contains("code-input")) {
          previousInput.focus()
        }
      } else {
        // Clear the current input value
        input.value = ""
      }
      this.updateCodeInput()
    }
  }

  handleFocus(event) {
    // Check if the focus was triggered by a user action (e.g., click or tab)
    if (event.relatedTarget && event.relatedTarget.classList.contains("code-input")) {
      return // Do not override focus if coming from another input
    }

    // Find the first empty input
    const codeInputs = Array.from(this.element.querySelectorAll(".code-input-container input"))
    const firstEmptyInput = codeInputs.find(input => input.value === "")

    // If there is an empty input, focus it; otherwise, focus the last input
    if (firstEmptyInput) {
      firstEmptyInput.focus()
    } else {
      codeInputs[codeInputs.length - 1].focus()
    }
  }

  handlePaste(event) {
    event.preventDefault() // Prevent the default paste behavior

    const pasteData = event.clipboardData.getData("text").slice(0, 6) // Limit to 6 characters
    const codeInputs = Array.from(this.element.querySelectorAll(".code-input-container input"))

    pasteData.split("").forEach((char, index) => {
      if (codeInputs[index]) {
        codeInputs[index].value = char
      }
    })

    // Focus the next empty input box
    const nextEmptyInput = codeInputs.find(input => input.value === "")
    if (nextEmptyInput) {
      nextEmptyInput.focus()
    }

    this.updateCodeInput()
  }

  updateCodeInput() {
    const codeInputs = Array.from(this.element.querySelectorAll(".code-input-container input"))
    const code = Array.from(codeInputs).map(input => input.value).join("")
    
    this.codeInputTarget.value = code

    if (this.autoSubmitValue && code.length === 6) {
      const form = this.codeInputTarget.closest("form")
      form?.requestSubmit()
    }
  }
}